import React, { useState } from "react";
import { DatePicker } from "antd";
import { AiOutlineCalendar } from "react-icons/ai";
import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";

import DropDownNew from "../Form/DropDownNew";
import CustomButton from "../Form/CustomButton";
import Input from "../Form/Input";

import "antd/dist/reset.css"; // for Ant Design v5+

const { RangePicker } = DatePicker;

// Custom Date Range Picker
const CustomDateRangePicker = ({ startDate, endDate, onRangeChange }) => {
  return (
    <div className="flex items-center gap-2">
      <RangePicker
        onChange={onRangeChange}
        value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : []}
        format="YYYY-MM-DD"
        allowClear
        className="border border-gray-300 rounded-md px-[15px] w-[240px] h-10"
      />
    </div>
  );
};

const TableHeader = ({
  searchValue,
  onSearchChange,
  onSearchSubmit,
  searchPlaceholder = "Search...",
  searchTooltip = "Type to search and press Enter",
  filters = [],
  dateRangeFilter = null,
  buttons = [],
  datePicker = null,
  containerClass = "",
  searchInputClass = "",
  filterDropdownClass = "",
  datePickerClass = "",
  datePickerInputClass = "",
  buttonWrapperClass = "",
  buttonClass = "",
}) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const hasSearch = typeof onSearchChange === "function";

  const handleRangeChange = (dates) => {
    if (dates) {
      const [start, end] = dates;
      setStartDate(start ? start.toDate() : null);
      setEndDate(end ? end.toDate() : null);
      dateRangeFilter?.onChange?.(dates);
    } else {
      setStartDate(null);
      setEndDate(null);
      dateRangeFilter?.onChange?.([]);
    }
  };

  return (
    <div
      className={twMerge(
        "w-full bg-white rounded-lg p-4 shadow-sm flex  gap-3 items-center justify-start",
        containerClass
      )}
    >
      {/* Search */}
      {hasSearch && (
        <div className="flex items-center w-full md:w-auto flex-grow md:flex-grow-0 gap-2">
          <Input
            type="text"
            placeholder={searchPlaceholder}
            title={searchTooltip}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSearchSubmit?.()}
            outerContainerStyle="relative top-[6px]"
            inputStyle={twMerge(
              `h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] pl-10 pr-3 border border-[#CECECE80]  py-2 rounded-[5px] text-sm placeholder:opacity-100 placeholder:text-[#9D9D9D] text-[14px] sm:placeholder:text-[11px] md:placeholder:text-[13px] lg:placeholder:text-[15px] focus:outline-none focus:border-indigo-500`,
              searchInputClass
            )}
            className="text-sm"
            searchIcon={true}
          />
        </div>
      )}

      {/* Date Range  */}
      {dateRangeFilter && (
        <div className={twMerge("w-full md:w-auto", datePickerClass)}>
          <DropDownNew
            name="dateRange"
            // label="Date Range"
            options={dateRangeFilter.options}
            defaultOption={
              dateRangeFilter.value
                ? { label: dateRangeFilter.value, value: dateRangeFilter.value }
                : null
            }
            onSelect={(val) => dateRangeFilter.onChange(val?.value)}
            placeholder="Filter by Date Range"
            displayKey="label"
            idKey="value"
            isForm
            buttonClassName={twMerge(
              "h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] w-full md:w-[180px] 2xl:w-[240px] text-[13px] rounded-[5px] border border-[#CECECE80]",
              filterDropdownClass
            )}
            dropdownClassName={twMerge(
              "w-full md:w-[180px] mt-1 text-[#9D9D9D] rounded-[0px]"
            )}
            labelStyle="text-sm"
            placeholderClass="text-[10px] sm:text-[11px] md:text-[13px] lg:text-[15px] text-[#9D9D9D]"
          />
        </div>
      )}

      {/* Filters */}
      {filters?.length > 0 &&
        filters.map((filter, index) => (
          <div className="w-full md:w-auto" key={index}>
            <DropDownNew
              name={filter.name}
              //label={filter.name}
              options={filter.options}
              defaultOption={
                filter.value
                  ? { label: filter.value, value: filter.value }
                  : null
              }
              onSelect={(val) => filter.onChange(val?.value)}
              placeholder={`Filter by ${filter.name}`}
              displayKey="label"
              idKey="value"
              isForm
              buttonClassName={twMerge(
                "h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px]  text-[13px]  rounded-[5px] border border-[#CECECE80]",
                filterDropdownClass
              )}
              dropdownClassName={twMerge(
                "w-full md:w-full  text-[#9D9D9D] rounded-[0px]"
              )}
              labelStyle="text-sm"
              placeholderClass="text-[10px] sm:text-[11px] md:text-[13px] lg:text-[14px] xl:text-[15px] text-[#9D9D9D]"
            />
          </div>
        ))}

      {/* Date Picker */}
      {datePicker && (
        <div className={twMerge("w-full md:w-auto", datePickerClass)}>
          <RangePicker
            value={[
              datePicker.startDate ? dayjs(datePicker.startDate) : null,
              datePicker.endDate ? dayjs(datePicker.endDate) : null,
            ]}
            format="YYYY-MM-DD"
            onChange={(dates) => {
              if (dates) {
                datePicker.onChange("startDate", dates[0]?.toDate());
                datePicker.onChange("endDate", dates[1]?.toDate());
              } else {
                datePicker.onChange("startDate", null);
                datePicker.onChange("endDate", null);
              }
            }}
            suffixIcon={
              <span className="cursor-pointer">
                <AiOutlineCalendar size={12} style={{ color: "#7B7B7B" }} />
              </span>
            }
            className={twMerge(
              "h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] rounded-[5px] border border-[#C5C5C5] text-gray-800",
              "w-full sm:w-[226px] md:w-[256px] lg:w-[296px] ",
              datePickerInputClass
            )}
            style={{
              backgroundColor: "white",
            }}
            placeholder={["Start date", "End date"]}
            inputReadOnly={true}
          />
        </div>
      )}

      {/* Buttons */}
      {buttons?.length > 0 && (
        <div
          className={twMerge(
            "flex gap-2",
            "  w-full text-[8px] sm:text-[9px] md:text-[10px] lg:text-[11px]",
            buttonWrapperClass
          )}
        >
          {buttons.map((btn, index) => (
            <CustomButton
              key={index}
              buttonText={btn.label}
              onClick={btn.onClick}
              className={twMerge(
                btn.className ||
                  (btn.variant === "secondary"
                    ? "bg-[#8D4DFF] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-3 rounded-[3.77px] w-[100px] uppercase"
                    : "bg-[#B088FF] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-3 rounded-[3.77px]"),
                !btn.className && buttonClass
              )}
              loading={btn.loading}
              disabled={btn.disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TableHeader;